'use client';

import React, { createContext, useContext, ReactNode, useState, useCallback } from 'react';
import { MeResponse } from '@/libs/api/auth';
import { useAsyncEffect } from '@/hooks/use-async-effect';
import { unstable_isUnrecognizedActionError, usePathname } from 'next/navigation';
import api from '@/api';
import { doNothing } from '@/utils/function';

interface AuthContextProps {
  isLoading: boolean;
  me?: MeResponse;
  setMe: (me: MeResponse) => void;
  refresh: () => void;
}

const AuthContext = createContext<AuthContextProps>({
  isLoading: true,
  me: undefined,
  setMe: doNothing,
  refresh: doNothing,
});

interface Props {
  children: ReactNode;
}

export const AuthProvider = (props: Props) => {
  const { children } = props;
  const [me, setMe] = useState<MeResponse>();
  const [refreshState, setRefreshState] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const pathname = usePathname();

  const refresh = useCallback(() => {
    setRefreshState(state => state + 1);
  }, []);

  useAsyncEffect(async () => {
    setLoading(true);

    try {
      setMe(await api.auth.me());
    } catch(e: any) {
      setMe(undefined);
    } finally {
      setLoading(false);
    }
  }, [pathname, refreshState]);

  return (
    <AuthContext.Provider value={{ isLoading, me, setMe, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};